import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDocuments } from '../contexts/DocumentContext';
import { parseScreenRoute } from '../utils/routeParsing';

/**
 * The screen-context payload sent with every assistant turn. Deterministic —
 * built entirely from route/state the frontend already has, no AI involved.
 * Replaces both of the old ad-hoc detectors (the URL regex in
 * routeParsing.ts and the independent string-matcher that used to live in
 * ChatSupportContext). Any id here without a name attached is resolved by
 * the backend in one query (see assistantService.buildScreenContextSentence)
 * so the model never has to guess a name from an id.
 */
export interface ScreenContext {
  pathname: string;
  customerId?: string;
  projectId?: string;
  reportTitle?: string;
  reportWorkflowType?: string;
  reportDocumentNames?: string[];
  reportContent?: string;
  /** Saved report id (from ?reportId=) — lets Arie act on "this report". */
  reportId?: string;
  comparisonDocumentNames?: string[];
  selectedDocumentNames?: string[];
}

export function useScreenContext(): ScreenContext {
  const location = useLocation();
  const { selectedDocuments } = useDocuments();

  return useMemo(() => {
    const route = parseScreenRoute(location.pathname);
    const context: ScreenContext = { pathname: location.pathname };

    if (route.kind === 'report') {
      const state = location.state as any;
      context.reportTitle = state?.reportTitle || 'Current Workflow Report';
      const reportId = new URLSearchParams(location.search).get('reportId') || state?.reportId;
      if (reportId) context.reportId = reportId;
      context.reportWorkflowType = state?.workflowType || undefined;
      if (Array.isArray(state?.documents) && state.documents.length > 0) {
        context.reportDocumentNames = state.documents.map((d: any) => d.name).filter(Boolean);
      }
      const rawContent = state?.isUniversalJson && state?.universalJsonResponse
        ? state.universalJsonResponse
        : state?.content;
      if (rawContent) {
        context.reportContent = typeof rawContent === 'string' ? rawContent : JSON.stringify(rawContent);
      }
      return context;
    }

    if (route.kind === 'comparison') {
      if (selectedDocuments && selectedDocuments.length > 0) {
        context.comparisonDocumentNames = selectedDocuments.map((d) => d.name);
      }
      return context;
    }

    if (route.kind === 'customer' || route.kind === 'project') {
      context.customerId = route.customerId;
      context.projectId = route.projectId;
      if (selectedDocuments && selectedDocuments.length > 0) {
        context.selectedDocumentNames = selectedDocuments.map((d) => d.name);
      }
      return context;
    }

    if (selectedDocuments && selectedDocuments.length > 0) {
      context.selectedDocumentNames = selectedDocuments.map((d) => d.name);
    }
    return context;
  }, [location.pathname, location.search, location.state, selectedDocuments]);
}
